import * as React from 'react';

import * as UI from 'core/ui';

import { Res, AddressDTO, BuyerCustomerDTO } from 'core/api';
import { Keypad, ThemeManager, ValueButtonGrid } from 'pos/ui';

import CloseDocumentScreen, { State, PaymentsList, ErrorMessage } from './ShowCloseDocumentScreen.Common';
import { Discounts } from './Invoice';
import { Col, Cols, Flex } from 'core/ui';
import { SessionProvider } from 'core/infrastructure';

function ShowAmount(props: { amount: number, text: string, bold?: boolean }) {
	return <>
		<Col>
			{props.bold ? <span><strong>{props.text}</strong></span> : props.text}
		</Col>
		<Col className='text-right' width='*'>
			{props.bold ? <span><strong>{UI.Format.asMoney(props.amount)}</strong></span>
				: <span>{UI.Format.asMoney(props.amount)}</span>}
		</Col>
	</>;
}

function PaymentStatus({ status }: { status: State }) {
	return <>
		<Cols className='text-26'>
			<ShowAmount amount={status.PendingAmount} text={Res.Web.CLOSE_TICKET_PENDING_LBL_2()} bold />
		</Cols>
		<Cols className='text-18'>
			<ShowAmount amount={status.AmountToPay} text={Res.Web.CLOSE_TICKET_TOTAL_LBL_2()} bold />
		</Cols>
		<Cols className='text-18'>
			<ShowAmount amount={status.PaidAmount} text={Res.Web.CLOSE_TICKET_PAID_LBL()} />
		</Cols>
		<Cols className='text-18'>
			<ShowAmount amount={status.ChangeAmount} text={Res.Web.CLOSE_TICKET_CHANGE_LBL_2()} />
		</Cols>
	</>;
}

function CustomerAndDiscounts({ discounts, customer }: { discounts?: Discounts, customer: BuyerCustomerDTO }) {
	return <div className='m-t-xl'>
		{discounts && discounts.discountPercentage !== 0
			? <Cols>
				<Col>
					{'Dto. %'}
				</Col>
				<Col className='text-right m-l' width='*'>
					<span>{UI.Format.asPercentage(discounts.discountPercentage)} <small>({UI.Format.asMoney(discounts.discountPercentageInCash)})</small></span>
				</Col>
			</Cols> : null}
		{discounts && discounts.cashDiscount !== 0
			? <Cols>
				<Col>
					{'Dto. €'}
				</Col>
				<Col className='text-right m-l' width='*'>
					<span>{UI.Format.asMoney(discounts.cashDiscount)}</span>
				</Col>
			</Cols> : null}
		{customer.Id !== 0
			? <>
				<Cols className='m-t'>
					<Col>
						<label><strong>{customer.FiscalName}</strong></label>
					</Col>
				</Cols>
				<Cols>
					<Col>
						<label>{getAddres(customer.Address)}</label>
					</Col>
				</Cols>
			</> : null}
	</div>;
}

function getAddres(address: AddressDTO) {
	if (address.ZipCode === '' && address.City === '') {
		return address.Street;
	}

	return `${address.Street} - ${address.ZipCode} ${address.City}`;
}

export default class TabletCloseDocumentScreen extends CloseDocumentScreen {

	protected useKeypad() {
		return false;
	}

	protected afterPaymentAdded = () => this.sendFocusToIntroducedAmount();
	protected afterPaymentRemoved = () => this.sendFocusToIntroducedAmount();

	private sendFocusToIntroducedAmount() {
		// Muy curioso. Si lo haces directamente, no funciona.
		// Con un timeout de 0, tampoco. Diría que no es problema del keypad
		// si no los remount que se hacen desde la parte de la parte de la
		// pantalla, pero no estoy seguro.
		// En las pruebas que he heco con 10ms basta, pero da bastante miedo.
		setTimeout(() => this.txtIntroducedAmount.current!.activate(), 60);
	}

	protected getContent(): React.ReactNode {
		const { PriceBox, CheckBox } = UI.controlsFor<State>();
		const hidePrintGiftCheck = ['CreateRefund', 'RefundInvoice'].includes(this.props.layout) || !SessionProvider.allowPrintTicketGift();

		// Remonto las cantidades introducidas cuando cambia mi estado, si no aunque añada pagos
		// no se refrescan los textBox
		const tipInput = <Flex.Horizontal>
			<Flex.Item>
				<UI.Button onClick={this.onSetTip}><i className='fa fa-arrow-right text-info' /></UI.Button>
			</Flex.Item>
			<Flex.Item size='*'>
				<PriceBox placeholder={Res.Web.CLOSE_TICKET_TIP_LBL()} ref={this.txtIntroducedTip} linkTo={x => x.CurrentTipAmount} boundValue={this.state.CurrentTipAmount || null} />
			</Flex.Item>
		</Flex.Horizontal>;

		const paidAndTipInput = <Flex.Vertical>
			<Flex.Item>
				<PriceBox placeholder={Res.Web.CLOSE_TICKET_PAID_LBL()} ref={this.txtIntroducedAmount} linkTo={x => x.CurrentPaidAmount} boundValue={this.state.CurrentPaidAmount || null}
					onKeyDown={UI.whenEnterKey(() => this.txtIntroducedTip.current!.activate())} />
			</Flex.Item>
			<Flex.Item className='m-l m-t-sm'>
				{tipInput}
			</Flex.Item>
		</Flex.Vertical >;

		const payments = <Flex.Vertical>
			<Flex.Item size='*'>
				<PaymentsList payments={this.state.Payments} selectedPaymentIndex={this.state.SelectedMethod} onSelect={this.onSelectPayment} onAddNote={this.onAddNote} onRemove={this.onRemovePayment} />
			</Flex.Item>

			<Flex.Item>
				<ErrorMessage errorMsg={this.state.ErrorMessage} />
			</Flex.Item>

			<Flex.Item key={this.state.proposedAmountKey} className='m-t-sm m-b-sm'>
				{paidAndTipInput}
			</Flex.Item>

			<Flex.Item>
				<Keypad isFullScreen={true} isLargeDevice={ThemeManager.isLargeDevice()} alwaysVisible />
			</Flex.Item>

		</Flex.Vertical>;

		return <Flex.Horizontal>
			<Flex.Item size='325px' className='border-right'> {/* 325px ajustado al pixel */}
				{payments}
			</Flex.Item >
			<Flex.Item size='*' className='border-right'>
				<ValueButtonGrid rows={'fill'} values={this.props.paymentOperation.Methods} onValueSelected={this.onPaymentMethodSelected} />
			</Flex.Item>
			<Flex.Item size='300px'>
				<Flex.Vertical>
					<Flex.Item>
						<PaymentStatus status={this.state} />
					</Flex.Item>
					<Flex.Item size='*'>
						<CustomerAndDiscounts customer={this.props.getDocument().customer} discounts={this.props.getDocument().discounts} />
					</Flex.Item>
					{
						hidePrintGiftCheck
							? null
							: <Flex.Item className='p-l p-b p-t text-16'>
								<CheckBox label={Res.Web.CLOSE_TICKET_PAID_PRINT_GIFT_CHK()} ref={this.chkPrintGift} linkTo={x => x.PrintGift} onValueChanged={this.onPrintGiftChanged} boundValue={this.state.PrintGift} />
							</Flex.Item>
					}
				</Flex.Vertical>
			</Flex.Item>
		</Flex.Horizontal >;
	}
}
